/**
 * GlobalView — AuthGuard
 *
 * Route guard that redirects based on auth status (§25.2).
 * - Loading: shows spinner
 * - Unauthenticated: redirect to /login
 * - Authenticated + PENDING: redirect to /pending
 * - Authenticated + REJECTED/SUSPENDED: redirect to /rejected
 * - Authenticated + ACTIVE: render children
 */

import { type ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import LoadingScreen from '../components/ui/LoadingScreen';

interface AuthGuardProps {
  children: ReactNode;
  requireAdmin?: boolean;
}

export default function AuthGuard({ children, requireAdmin }: AuthGuardProps) {
  const authStatus = useAuthStore((s) => s.authStatus);
  const userStatus = useAuthStore((s) => s.userStatus);
  const role = useAuthStore((s) => s.role);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (authStatus === 'loading' || authStatus === 'idle') return;

    if (authStatus === 'unauthenticated') {
      navigate('/login', { replace: true });
      return;
    }

    // Authenticated — check user status
    if (userStatus === 'pending') {
      navigate('/pending', { replace: true });
      return;
    }

    if (userStatus === 'rejected' || userStatus === 'suspended') {
      navigate('/rejected', { replace: true });
      return;
    }

    // Admin check
    if (requireAdmin && role !== 'admin') {
      navigate('/', { replace: true });
      return;
    }
  }, [authStatus, userStatus, role, requireAdmin, navigate, location.pathname]);

  // Loading state — spy cockpit HUD
  if (authStatus === 'idle' || authStatus === 'loading') {
    return <LoadingScreen />;
  }

  // Don't render if redirecting
  if (authStatus === 'unauthenticated') return null;
  if (userStatus === 'pending') return null;
  if (userStatus === 'rejected' || userStatus === 'suspended') return null;
  if (requireAdmin && role !== 'admin') return null;

  return <>{children}</>;
}
